export const SELF_DOG_P1_PRICE_CENTS = 1270000;
export const SELF_DOG_P1_PRICE_LABEL = '$12,700';
export const SELF_DOG_E1_PRICE_CENTS = 585000;
export const SELF_DOG_E1_PRICE_LABEL = '$5,850';
export const SELF_BOT_X1_PRICE_CENTS = 17000000;
export const SELF_BOT_X1_PRICE_LABEL = '$170,000';
export const SELF_BOT_P1_PRICE_CENTS = 4950000;
export const SELF_BOT_P1_PRICE_LABEL = '$49,500';

export type RoboticsProductId =
  | 'self-bot-x1'
  | 'self-bot-p1'
  | 'self-dog-p1'
  | 'self-dog-e1';
export type RoboticsVariantId = 'pro' | 'edu';

export type RoboticsSpec = {
  parameter: string;
  specification: string;
};

export type RoboticsComparisonSpec = {
  parameter: string;
  pro: string;
  edu: string;
};

export type RoboticsVariant = {
  id: RoboticsVariantId;
  name: string;
  summary: string;
};

export type RoboticsProduct = {
  id: RoboticsProductId;
  name: string;
  tagline: string;
  shortDescription: string;
  description: string;
  priceCents: number;
  priceLabel: string;
  images: { src: string; alt: string }[];
  bestFor: string[];
  highlights: string[];
  specs?: RoboticsSpec[];
  comparisonSpecs?: RoboticsComparisonSpec[];
  simplifiedSpecs?: RoboticsSpec[];
  variants?: RoboticsVariant[];
};

export const ROBOTICS_PRODUCTS: RoboticsProduct[] = [
  {
    id: 'self-bot-x1',
    name: 'SELF Bot X1',
    tagline:
      'A flagship humanoid robotics platform for enterprise AI, research, and the future of human-robot interaction',
    shortDescription:
      'The SELF Bot X1 is a premium humanoid robotics platform designed for enterprise, research, and advanced AI deployment. With full-body mobility, sophisticated perception, onboard high-performance computing, and interactive communication features, the SELF Bot X1 is built for organizations developing the next generation of embodied AI solutions.',
    description: `Advanced enterprise humanoid robot for embodied AI, research, premium automation, and next-generation human-robot interaction.

The SELF Bot X1 is a premium AI-ready humanoid robotics platform built for advanced enterprise deployments, research environments, innovation labs, and high-end customer-facing applications. Designed for organizations exploring the future of embodied AI, the SELF Bot X1 combines humanoid mobility, intelligent sensing, natural interaction capabilities, and high-performance onboard computing in a sophisticated full-body robotics platform.

With a height of 169 cm, approximately 69 kg in weight, and 40 total degrees of freedom, the SELF Bot X1 is engineered to support dynamic movement, expressive interaction, and advanced robotic functionality. Its perception system includes 3D LiDAR, RGB-D cameras, and fisheye cameras, enabling environmental awareness and spatial sensing for intelligent navigation and interaction.

The SELF Bot X1 is equipped with powerful onboard processing, including a 16-core high-performance CPU and an NVIDIA Jetson Orin 64G, making it well-suited for AI inference, robotics development, autonomy workflows, and custom software integration. Built-in communication features such as Wi-Fi, 4G/5G, a facial interaction screen, array microphones, speaker, and indicator lights help create a more engaging and human-like user experience.

Whether used for enterprise AI pilots, robotics research, premium demonstrations, advanced education, or immersive customer-facing installations, the SELF Bot X1 is positioned as a flagship humanoid platform for organizations building the future of robotics.`,
    priceCents: SELF_BOT_X1_PRICE_CENTS,
    priceLabel: SELF_BOT_X1_PRICE_LABEL,
    images: [
      {
        src: '/assets/images/robotics/self-bot-x1-main.png',
        alt: 'SELF Bot X1 humanoid robot — front view',
      },
      {
        src: '/assets/images/robotics/self-bot-x1-2.png',
        alt: 'SELF Bot X1 humanoid robot — profile view',
      },
      {
        src: '/assets/images/robotics/self-bot-x1-3.png',
        alt: 'SELF Bot X1 humanoid robot — detail view',
      },
      {
        src: '/assets/images/robotics/self-bot-x1-4.png',
        alt: 'SELF Bot X1 humanoid robot — interaction view',
      },
      {
        src: '/assets/images/robotics/self-bot-x1-5.png',
        alt: 'SELF Bot X1 humanoid robot — full body view',
      },
    ],
    bestFor: [
      'Enterprise AI pilots',
      'Embodied AI research',
      'Robotics labs and R&D',
      'Premium customer-facing deployments',
      'Innovation centers and technology showcases',
      'Advanced education and university programs',
      'Human-robot interaction research',
      'Multi-robot coordination and swarm experimentation',
    ],
    highlights: [
      'Full-size humanoid robot platform',
      'Advanced enterprise-grade AI and robotics hardware',
      '40 total degrees of freedom',
      '3D LiDAR, RGB-D camera, and fisheye camera sensing system',
      'Facial screen with expression display for interactive engagement',
      'Wi-Fi and 4G/5G connectivity',
      'Powered by a 16-core CPU and NVIDIA Jetson Orin 64G',
      'Supports fast charging and swappable battery operation',
      'Includes handheld remote, smart OTA upgrades, dance development, and swarm control software',
      'Designed for advanced robotics development and high-value commercial applications',
    ],
    simplifiedSpecs: [
      { parameter: 'Model', specification: 'SELF Bot X1' },
      { parameter: 'Platform', specification: 'AGIBOT A2 Ultra' },
      { parameter: 'Price', specification: '$170,000.00' },
      { parameter: 'Height', specification: '169 cm' },
      { parameter: 'Weight', specification: 'Approx. 69 kg' },
      { parameter: 'DOF', specification: '40 total DOF' },
      {
        parameter: 'Sensors',
        specification: '3D LiDAR, RGB-D camera, fisheye camera',
      },
      { parameter: 'Connectivity', specification: 'Wi-Fi, 4G/5G' },
      { parameter: 'Max Speed', specification: 'Up to 1.2 m/s' },
      { parameter: 'Max Arm Load', specification: 'Approx. 2 kg' },
      { parameter: 'Battery', specification: '14.4 Ah' },
      {
        parameter: 'Runtime',
        specification: 'Up to 3 hours standing / 1.5+ hours walking',
      },
      {
        parameter: 'Charging',
        specification: '2 hours, fast charging, swappable battery',
      },
      {
        parameter: 'Compute',
        specification: '16-core CPU + NVIDIA Jetson Orin 64G',
      },
      {
        parameter: 'Interaction',
        specification: 'Facial screen, array microphone, speaker, indicator light',
      },
      {
        parameter: 'Extras',
        specification:
          'Handheld remote, OTA upgrades, dance development, swarm control support',
      },
    ],
    specs: [
      { parameter: 'Base Hardware Model', specification: 'AGIBOT A2 Ultra' },
      { parameter: 'SELF Labs Product Name', specification: 'SELF Bot X1' },
      { parameter: 'Price', specification: '$170,000.00' },
      { parameter: 'Dimensions', specification: '169 cm (H) × 75 cm (W) × 30 cm (L)' },
      { parameter: 'Height', specification: '169 cm' },
      { parameter: 'Weight', specification: 'Approx. 69 kg' },
      { parameter: 'Total Degrees of Freedom', specification: '40' },
      { parameter: 'Neck DOF', specification: '2' },
      { parameter: 'Single Arm DOF', specification: '7' },
      { parameter: 'Single Leg DOF', specification: '6' },
      { parameter: 'Single Hand', specification: '6-DOF dexterous hand' },
      { parameter: '3D LiDAR', specification: 'Equipped' },
      { parameter: 'RGB-D Camera', specification: 'Equipped' },
      { parameter: 'Fisheye Camera', specification: 'Equipped' },
      { parameter: 'Communication Method', specification: 'Wi-Fi, 4G/5G' },
      {
        parameter: 'Interaction Screen',
        specification: 'Facial screen with expression display',
      },
      { parameter: 'Microphone', specification: 'Array microphone' },
      { parameter: 'Speaker', specification: 'Equipped' },
      { parameter: 'Indicator Light', specification: 'Equipped' },
      { parameter: 'Peak Knee Torque', specification: '270 N·m' },
      { parameter: 'Max Speed', specification: 'Up to 1.2 m/s' },
      { parameter: 'Max Arm Load', specification: 'Approx. 2 kg' },
      { parameter: 'Battery Capacity', specification: '14.4 Ah' },
      { parameter: 'Standing Time', specification: 'Up to 3 hours' },
      { parameter: 'Walking Time', specification: '1.5 hours or more' },
      {
        parameter: 'Power Supply',
        specification: 'Fast charging and swappable battery supported',
      },
      { parameter: 'Charging Time', specification: '2 hours' },
      { parameter: 'Input Voltage', specification: '110 V–220 V' },
      { parameter: 'Charger Output', specification: '54.6V / 8A' },
      { parameter: 'Basic Board', specification: '16-core high-performance CPU' },
      { parameter: 'High-Performance Board', specification: 'NVIDIA Jetson Orin 64G' },
      { parameter: 'Handheld Remote', specification: 'Yes' },
      { parameter: 'Smart OTA Upgrade', specification: 'Yes' },
      { parameter: 'Dance Development', specification: 'Supported' },
      { parameter: 'Swarm Control Software', specification: 'Supported' },
    ],
  },
  {
    id: 'self-bot-p1',
    name: 'SELF Bot P1',
    tagline:
      'A professional humanoid platform for AI demos, education, and real-world robotics pilots',
    shortDescription:
      'The SELF Bot P1 is a professional humanoid robotics platform designed for AI demonstrations, education, research, and commercial pilot programs. With full-size humanoid movement, intelligent control, remote operation, and software-ready capabilities, the SELF Bot P1 gives teams an accessible way to explore embodied AI and real-world robotics.',
    description: `Professional humanoid robot for AI demos, research, education, and commercial pilot programs.

The SELF Bot P1 is a professional humanoid robotics platform built for organizations exploring embodied AI, robotics research, human-robot interaction, education, and commercial automation pilots. Designed as a more accessible humanoid option in the SELF Labs robotics lineup, the SELF Bot P1 delivers full-body humanoid mobility, intelligent control, remote operation, and software-ready capabilities for real-world experimentation.

With a height of 169 cm, approximately 64 kg body weight, and 23 total degrees of freedom, the SELF Bot P1 provides a strong foundation for movement, interaction, demonstrations, and robotics development. It is equipped with Wi-Fi communication, a built-in speaker, handheld remote support, smart OTA upgrades, dance development capabilities, and swarm control software support.

The SELF Bot P1 is ideal for schools, innovation labs, robotics programs, showrooms, research teams, and businesses that want to begin working with humanoid robotics without moving directly into the flagship enterprise tier.`,
    priceCents: SELF_BOT_P1_PRICE_CENTS,
    priceLabel: SELF_BOT_P1_PRICE_LABEL,
    images: [
      {
        src: '/assets/images/robotics/self-bot-p1-main.png',
        alt: 'SELF Bot P1 humanoid robot — front view',
      },
      {
        src: '/assets/images/robotics/self-bot-p1-2.png',
        alt: 'SELF Bot P1 humanoid robot — profile view',
      },
      {
        src: '/assets/images/robotics/self-bot-p1-3.png',
        alt: 'SELF Bot P1 humanoid robot — detail view',
      },
      {
        src: '/assets/images/robotics/self-bot-p1-4.png',
        alt: 'SELF Bot P1 humanoid robot — interaction view',
      },
      {
        src: '/assets/images/robotics/self-bot-p1-5.png',
        alt: 'SELF Bot P1 humanoid robot — full body view',
      },
    ],
    bestFor: [
      'AI and robotics demonstrations',
      'Education and university robotics programs',
      'Human-robot interaction research',
      'Innovation labs and technology showcases',
      'Commercial pilot programs',
      'Customer-facing demo environments',
      'Robotics software testing',
      'Swarm control experiments',
    ],
    highlights: [
      'Professional humanoid robot platform',
      '169 cm full-size humanoid design',
      '23 total degrees of freedom',
      'Wi-Fi communication',
      'Built-in speaker',
      'Handheld remote included',
      'Smart OTA upgrade support',
      'Dance development supported',
      'Swarm control software supported',
      'Fast charging and swappable battery support',
      'Designed for education, research, demos, and commercial robotics pilots',
    ],
    simplifiedSpecs: [
      { parameter: 'Model', specification: 'SELF Bot P1' },
      { parameter: 'Platform', specification: 'AGIBOT A2 Lite' },
      { parameter: 'Price', specification: '$49,500.00' },
      { parameter: 'Height', specification: '169 cm' },
      { parameter: 'Weight', specification: 'Approx. 64 kg' },
      { parameter: 'DOF', specification: '23 total DOF' },
      { parameter: 'Connectivity', specification: 'Wi-Fi' },
      { parameter: 'Max Speed', specification: 'Up to 0.8 m/s' },
      { parameter: 'Max Arm Load', specification: 'Approx. 2 kg' },
      { parameter: 'Battery', specification: '14.4 Ah' },
      {
        parameter: 'Runtime',
        specification: 'Up to 4.5 hours standing / 1.5+ hours walking',
      },
      {
        parameter: 'Charging',
        specification: '2 hours, fast charging, swappable battery',
      },
      { parameter: 'Compute', specification: '16-core high-performance CPU' },
      { parameter: 'Controls', specification: 'Handheld remote included' },
      {
        parameter: 'Software',
        specification: 'OTA upgrades, dance development, swarm control support',
      },
    ],
    specs: [
      { parameter: 'Base Hardware Model', specification: 'AGIBOT A2 Lite' },
      { parameter: 'SELF Labs Product Name', specification: 'SELF Bot P1' },
      { parameter: 'Price', specification: '$49,500.00' },
      { parameter: 'Dimensions', specification: '169 cm (H) × 75 cm (W) × 30 cm (L)' },
      { parameter: 'Height', specification: '169 cm' },
      { parameter: 'Weight', specification: 'Approx. 64 kg' },
      { parameter: 'Total Degrees of Freedom', specification: '23' },
      { parameter: 'Neck DOF', specification: '1' },
      { parameter: 'Single Arm DOF', specification: '5' },
      { parameter: 'Single Leg DOF', specification: '6' },
      { parameter: 'Communication Method', specification: 'Wi-Fi' },
      { parameter: 'Speaker', specification: 'Equipped' },
      { parameter: 'Peak Knee Torque', specification: '270 N·m' },
      { parameter: 'Max Speed', specification: 'Up to 0.8 m/s' },
      { parameter: 'Max Arm Load', specification: 'Approx. 2 kg' },
      { parameter: 'Battery Capacity', specification: '14.4 Ah' },
      { parameter: 'Standing Time', specification: 'Up to 4.5 hours' },
      { parameter: 'Walking Time', specification: '1.5 hours or more' },
      {
        parameter: 'Power Supply',
        specification: 'Fast charging and swappable battery supported',
      },
      { parameter: 'Charging Time', specification: '2 hours' },
      { parameter: 'Input Voltage', specification: '110 V–220 V' },
      { parameter: 'Charger Output', specification: '54.6V / 8A' },
      { parameter: 'Basic Board', specification: '16-core high-performance CPU' },
      { parameter: 'Handheld Remote', specification: 'Yes' },
      { parameter: 'Smart OTA Upgrade', specification: 'Yes' },
      { parameter: 'Dance Development', specification: 'Supported' },
      { parameter: 'Swarm Control Software', specification: 'Supported' },
    ],
  },
  {
    id: 'self-dog-p1',
    name: 'SELF Dog P1',
    tagline:
      'A professional robot dog platform for AI demos, robotics labs, and real-world automation pilots',
    shortDescription:
      'The SELF Dog P1 is a professional AI-ready quadruped robot built for education, demos, inspection pilots, and business automation testing. Designed for organizations exploring embodied AI, the SELF Dog P1 can be customized with SELF Labs software, AI agents, voice interaction, and branded deployment packages.',
    description: `AI-ready quadruped robot for demos, inspection pilots, robotics labs, and commercial automation.

The SELF Dog P1 is a professional quadruped robotics platform designed for businesses, schools, labs, and teams exploring real-world AI robotics. Built on an advanced four-legged robotic base, the SELF Dog P1 is ideal for demonstrations, research, security concepts, inspection workflows, customer-facing experiences, and AI agent integration.

With its compact robotic design, stable walking movement, camera-ready body, and developer-focused use cases, the SELF Dog P1 gives organizations a practical way to test how embodied AI can move through physical environments, collect information, interact with users, and support automated tasks.

SELF Labs can customize the SELF Dog P1 with AI software, voice interaction, remote operation workflows, autonomous task planning, and brand-specific deployment packages.`,
    priceCents: SELF_DOG_P1_PRICE_CENTS,
    priceLabel: SELF_DOG_P1_PRICE_LABEL,
    images: [
      {
        src: '/assets/images/robotics/self-dog-p1-main.png',
        alt: 'SELF Dog P1 quadruped robot — front view',
      },
      {
        src: '/assets/images/robotics/self-dog-p1-2.png',
        alt: 'SELF Dog P1 quadruped robot — side profile',
      },
      {
        src: '/assets/images/robotics/self-dog-p1-3.png',
        alt: 'SELF Dog P1 quadruped robot — detail view',
      },
      {
        src: '/assets/images/robotics/self-dog-p1-4.png',
        alt: 'SELF Dog P1 quadruped robot — action pose',
      },
    ],
    bestFor: [
      'Education and robotics labs',
      'AI demos and trade shows',
      'Security and inspection pilots',
      'Developer testing',
      'Concierge and customer-facing demos',
      'Research and automation experiments',
      'Enterprise robotics proof-of-concepts',
    ],
    highlights: [
      'Professional quadruped robot platform',
      'AI software integration options',
      'Remote operation and demo-ready use cases',
      'Suitable for indoor robotics testing',
      'Custom branding and deployment packages available',
      'Designed for research, education, and business pilots',
      'Can be paired with SELF Labs AI agents and automation tools',
    ],
    specs: [
      {
        parameter: 'SELF Labs Product Name',
        specification: 'SELF Dog P1',
      },
      {
        parameter: 'Product Positioning',
        specification: 'Compact yet powerful industry-grade quadruped robot',
      },
      {
        parameter: 'Application Scenarios',
        specification:
          'Special operations, emergency response, security, inspection, education, entertainment, robotics labs, AI demos, and enterprise pilots',
      },
      {
        parameter: 'Standing Dimensions',
        specification: '63 × 36 × 42 cm',
      },
      {
        parameter: 'Folded Dimensions',
        specification: '67 × 43 × 15 cm',
      },
      {
        parameter: 'Operation & Control',
        specification: 'Standard reinforcement-learning-based operation and control',
      },
      {
        parameter: 'Speed',
        specification: '0–3.7 m/s, with limit up to approximately 5 m/s',
      },
      {
        parameter: 'Static Load Capacity',
        specification: 'Approximately 8 kg, with limit up to approximately 10 kg',
      },
      {
        parameter: 'Unloaded Range',
        specification: 'Up to 6 km at 1.8 m/s',
      },
      {
        parameter: 'Loaded Range',
        specification: 'Up to 4 km with 5 kg payload',
      },
      {
        parameter: 'Protection Level',
        specification: 'IP54',
      },
      {
        parameter: 'Stable Operating Time in Extreme Environments',
        specification: 'Up to 200 hours',
      },
      {
        parameter: 'Peak Output Power',
        specification: 'Approximately 3,500 W',
      },
      {
        parameter: 'Joint Motor Peak Torque',
        specification: 'Approximately 48 Nm',
      },
      {
        parameter: 'Joint Motor Peak Power Density',
        specification: '2 kW/kg',
      },
      {
        parameter: 'Joint Control Mode',
        specification: 'Dual encoding',
      },
      {
        parameter: 'Expansion Interfaces',
        specification:
          'Ethernet port, USB port, power port 12V/24V, SBUS port, UART port',
      },
      {
        parameter: 'Supported Expansion Options',
        specification:
          '3D radar, depth camera, RTK, 4G module, image transmission module, follow-up module, application-layer payloads, and custom hardware integrations',
      },
    ],
  },
  {
    id: 'self-dog-e1',
    name: 'SELF Dog E1',
    tagline:
      'A compact quadruped robot built for education, demos, and next-generation robotics learning',
    shortDescription:
      'The SELF Dog E1 is an entry-level quadruped robot designed for education, robotics labs, AI demos, and developer experimentation. With a lightweight yet durable build, agile movement, and optional development-ready interfaces, the SELF Dog E1 is a practical platform for teams exploring real-world robotics and embodied AI.',
    description: `Entry-level quadruped robot for education, demos, robotics labs, and AI exploration.

The SELF Dog E1 is a compact, AI-ready quadruped robotics platform designed for education, demonstrations, research environments, and early-stage commercial testing. Built for teams exploring real-world robotics, the SELF Dog E1 offers stable movement, intelligent mobility, and a lightweight form factor that makes it ideal for classrooms, labs, developers, and organizations entering the robotics space.

With a compact aluminum alloy and high-strength engineering plastic body, the SELF Dog E1 is designed to deliver a balance of portability, performance, and affordability. It supports advanced locomotion capabilities including stair climbing, slope climbing, jumping, and a range of standard and special robot actions.

The SELF Dog E1 is a strong fit for schools, robotics programs, innovation labs, technology showcases, developer experimentation, and businesses looking to explore embodied AI without starting at enterprise-level pricing.

Available in Pro and Edu configurations, the SELF Dog E1 gives users the flexibility to choose between a general-use quadruped platform or a more development-focused version with expanded interface and secondary development support.`,
    priceCents: SELF_DOG_E1_PRICE_CENTS,
    priceLabel: SELF_DOG_E1_PRICE_LABEL,
    images: [
      {
        src: '/assets/images/robotics/self-dog-e1-main.png',
        alt: 'SELF Dog E1 quadruped robot',
      },
    ],
    bestFor: [
      'Education and STEM programs',
      'Robotics labs and research',
      'AI demonstrations',
      'Developer experimentation',
      'Trade show and event demos',
      'Early-stage security or inspection concepts',
      'Learning autonomous mobility workflows',
    ],
    highlights: [
      'Compact quadruped robot platform',
      'Built with aluminum alloy and high-strength engineering plastic',
      'Up to 3.7 m/s max speed',
      'Up to 8 kg max payload',
      'Up to 6 km range',
      'Standard actions and advanced motion capabilities',
      'Real-time image transmission support',
      'Available in Pro and Edu versions',
      'Edu version supports secondary development and expansion interfaces',
      'Well-suited for education, research, and entry-level robotics deployment',
    ],
    variants: [
      {
        id: 'pro',
        name: 'Pro',
        summary:
          'A streamlined quadruped platform for demos, mobility testing, and general robotics use.',
      },
      {
        id: 'edu',
        name: 'Edu',
        summary:
          'A development-oriented version designed for learning, experimentation, and advanced educational use, with secondary development support and expansion interfaces including Ethernet, USB, power, SBUS, and UART.',
      },
    ],
    simplifiedSpecs: [
      { parameter: 'Model', specification: 'SELF Dog E1' },
      { parameter: 'Variants', specification: 'Pro / Edu' },
      { parameter: 'Price', specification: '$5,850.00' },
      { parameter: 'Weight', specification: '15 kg' },
      { parameter: 'Speed', specification: 'Up to 3.7 m/s' },
      { parameter: 'Payload', specification: 'Approx. 8 kg' },
      { parameter: 'Range', specification: 'Up to 6 km' },
      { parameter: 'Battery Life', specification: '1–2 hours' },
      { parameter: 'Charging Time', specification: '≤ 2 hours' },
      { parameter: 'Protection', specification: 'IP32' },
      { parameter: 'Climbing Slope', specification: '30°' },
      { parameter: 'Stair Climb Height', specification: '16 cm' },
      { parameter: 'Jump Height', specification: '35 cm' },
      { parameter: 'Processor', specification: '8-core high-performance CPU' },
      { parameter: 'Warranty', specification: '1 year' },
    ],
    comparisonSpecs: [
      { parameter: 'Base Model', pro: 'AGIBOT D1 Pro', edu: 'AGIBOT D1 Edu' },
      { parameter: 'Price', pro: '$5,850.00', edu: '$5,850.00' },
      {
        parameter: 'Material',
        pro: 'Aluminum alloy + high-strength engineering plastic',
        edu: 'Aluminum alloy + high-strength engineering plastic',
      },
      {
        parameter: 'Standing Size (L × W × H)',
        pro: 'Approx. 610 × 370 × 406 mm',
        edu: 'Approx. 610 × 370 × 406 mm',
      },
      {
        parameter: 'Lying Size (L × W × H)',
        pro: 'Approx. 680 × 370 × 115 mm',
        edu: 'Approx. 680 × 370 × 115 mm',
      },
      { parameter: 'Weight (Including Battery)', pro: '15 kg', edu: '15 kg' },
      { parameter: 'Operating Temperature', pro: '0°C to 40°C', edu: '0°C to 40°C' },
      { parameter: 'Protection Level', pro: 'IP32', edu: 'IP32' },
      { parameter: 'Charging Time', pro: '≤ 2 hours', edu: '≤ 2 hours' },
      { parameter: 'Endurance Time', pro: '1–2 hours', edu: '1–2 hours' },
      { parameter: 'Range', pro: '6 km', edu: '6 km' },
      {
        parameter: 'Computing Power',
        pro: '8-core high-performance CPU',
        edu: '8-core high-performance CPU',
      },
      { parameter: 'Max Power Output', pro: '3500 W', edu: '3500 W' },
      { parameter: 'Max Speed', pro: '3.7 m/s', edu: '3.7 m/s' },
      { parameter: 'Max Payload', pro: 'Approx. 8 kg', edu: 'Approx. 8 kg' },
      { parameter: 'Continuous Stair Climb Height', pro: '16 cm', edu: '16 cm' },
      { parameter: 'Max Climbing Slope', pro: '30°', edu: '30°' },
      { parameter: 'Jumping Height (Off Ground)', pro: '35 cm', edu: '35 cm' },
      {
        parameter: 'Actuator Type',
        pro: 'High power density motion unit',
        edu: 'High power density motion unit',
      },
      { parameter: 'Max Joint Torque', pro: '48 N·m', edu: '48 N·m' },
      { parameter: 'Aluminum Alloy Precision Joint Motors', pro: '12', edu: '12' },
      { parameter: 'Joint Range (Body)', pro: '-28° to 28°', edu: '-28° to 28°' },
      { parameter: 'Joint Range (Thigh)', pro: '-170° to 66°', edu: '-170° to 66°' },
      { parameter: 'Joint Range (Lower Leg)', pro: '35° to 156°', edu: '35° to 156°' },
      {
        parameter: 'Camera FOV',
        pro: 'DFOV 122°, HFOV 111°, VFOV 70°',
        edu: 'DFOV 122°, HFOV 111°, VFOV 70°',
      },
      { parameter: 'IMU', pro: 'Standard', edu: 'Standard' },
      {
        parameter: 'Standard Actions',
        pro: 'Stand, lie down, kneel, prone, low posture, high posture, horizontal roll',
        edu: 'Stand, lie down, kneel, prone, low posture, high posture, horizontal roll',
      },
      {
        parameter: 'Special Actions',
        pro: 'Forward jump, upward jump, biped standing, backflip, waving, etc.',
        edu: 'Forward jump, upward jump, biped standing, backflip, waving, etc.',
      },
      { parameter: 'Real-Time Image Transmission', pro: 'Supported', edu: 'Supported' },
      { parameter: 'Secondary Development', pro: 'Not supported', edu: 'Supported' },
      {
        parameter: 'Expansion Interfaces',
        pro: 'None',
        edu: 'Ethernet, USB, Power Port, SBUS Port, UART Port',
      },
      {
        parameter: 'Battery',
        pro: 'Rated 5Ah, nominal 4.6Ah, voltage 43.2V',
        edu: 'Rated 5Ah, nominal 4.6Ah, voltage 43.2V',
      },
      { parameter: 'Adapter', pro: 'Standard', edu: 'Standard' },
      { parameter: 'Charging Base', pro: 'Standard', edu: 'Standard' },
      { parameter: 'Handheld Controller', pro: 'Standard', edu: 'Standard' },
      { parameter: 'Warranty', pro: '1 year', edu: '1 year' },
    ],
  },
];

export function getRoboticsProduct(id: string): RoboticsProduct | undefined {
  return ROBOTICS_PRODUCTS.find((product) => product.id === id);
}
